// src/js/main.js
import { initializeThemeToggle } from "./core/theme-toggle.js";
import { initializeAuth } from "./core/auth.js";
import { updateUnknownWordsList, initializeClearButton, initializeRemoveButtons } from "./core/wordListUI.js";
import { initializeStripeUI } from "./core/stripe.js"; // consider lazy loading if it gets big
import { initTTS } from "./core/elevenLabsTTS.js";
import { updateVoiceSelect } from "./core/voiceConfig.js";
import { initializeTranslateButtons } from "./core/translateParagraphs.js";
import {
  initializePreferences,
  getPreferences,
  setLanguagePreference,
  setLevelPreference,
} from "./core/preferencesManager.js";
import { initializeTooltip } from "./core/tooltip.js";
import { initializeDialogueGeneration } from "./core/generateDialogueUI.js";
import { initializeQuiz } from "./core/quizUI.js"; // consider lazy loading if it gets big
import { registerServiceWorker } from "./core/serviceWorker.js";

function checkGenerationLimit() {
  const usageInfoStr = localStorage.getItem("dialogue_usage_info");
  if (usageInfoStr) {
    const usageInfo = JSON.parse(usageInfoStr);

    // Check if the stored data is from the current hour
    const now = new Date();
    const lastUpdated = new Date(usageInfo.last_updated);

    // Reset the limit if it's a new hour
    const isSameHour =
      now.getUTCFullYear() === lastUpdated.getUTCFullYear() &&
      now.getUTCMonth() === lastUpdated.getUTCMonth() &&
      now.getUTCDate() === lastUpdated.getUTCDate() &&
      now.getUTCHours() === lastUpdated.getUTCHours();

    // Only apply the limit if it's from the current hour
    if (isSameHour && usageInfo.limit_reached) {
      document.body.classList.add("limit-reached");
    } else if (!isSameHour) {
      // Clear the old usage data if it's from a different hour
      localStorage.removeItem("dialogue_usage_info");
      document.body.classList.remove("limit-reached");

      // Re-enable the button if it was previously disabled due to limit
      if (usageInfo.limit_reached) {
        const generateBtn = document.getElementById("generateBtn");
        generateBtn.disabled = false;
      }
    }
  }
}

function initializeLanguageSelect() {
  const languageSelect = document.getElementById("language-select");

  languageSelect?.addEventListener("change", () => {
    setLanguagePreference(languageSelect.value);
    updateUnknownWordsList();
    document.querySelectorAll(".word.unknown").forEach((el) => {
      el.classList.remove("unknown");
    });
    document.getElementById("output").innerHTML = "";
    updateVoiceSelect(languageSelect.value);
  });
}

function initializeLevelSelect() {
  const levelSelect = document.getElementById("level-select");

  levelSelect?.addEventListener("change", () => {
    setLevelPreference(levelSelect.value);
    document.getElementById("output").innerHTML = "";
  });
}

export function initialize() {
  // Initialize preferences first
  initializePreferences();

  // Check generation limit state
  checkGenerationLimit();

  // Add periodic check for limit reset (every minute)
  setInterval(checkGenerationLimit, 60000);

  // Check limit when user returns to the tab
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") {
      checkGenerationLimit();
    }
  });

  // Load saved preferences
  const { language, level } = getPreferences();

  // Set initial values from preferences
  const languageSelect = document.getElementById("language-select");
  const levelSelect = document.getElementById("level-select");

  if (languageSelect && language) {
    languageSelect.value = language;
  }

  if (levelSelect && level) {
    levelSelect.value = level;
  }

  // Other Initialize
  initializeThemeToggle();
  initializeAuth();
  initializeStripeUI();
  initTTS();
  updateVoiceSelect(languageSelect.value);
  initializeDialogueGeneration();
  initializeClearButton();
  initializeLanguageSelect();
  initializeLevelSelect();
  initializeRemoveButtons();
  initializeTooltip();
  initializeTranslateButtons();
  initializeQuiz();

  // Initial UI updates
  updateUnknownWordsList();

  // Register service worker
  registerServiceWorker();
}
